import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  EmptyContent,
  Flex,
  Spinner,
  VisuallyHidden,
} from '@/components/ui';
import { Link, useNavigate } from 'react-router-dom';
import type { StudyForm } from '@/types/studyForms';
import { useStudyForms } from './hooks/useStudyForms';
import { useImportMappingData } from './hooks/useImportMappingData';
import { ImportMappingDebugPanel } from './components/debug/ImportMappingDebugPanel.tsx';
import {
  ImportFileMappings,
  ImportProcessingTimeline,
  ImportProgressLayout,
  ImportProgressStatus,
  ImportProgressCard,
  ImportStatisticsCards,
} from './components/import-progress';
import {
  IconAlertTriangleFilled,
  IconArrowLeft,
  IconFileSearch,
  IconHome,
} from '@tabler/icons-react';

interface ImportProgressPageProps {
  mappingId: string;
}

/**
 * ImportProgressPage (now ImportDetailsPage)
 *
 * Shows details of a created import mapping.
 *
 * API Usage:
 * - GET /api/v1/studies/:study_id/import_mappings/:id
 *
 * Features:
 * - Displays mapping information (ID, creation time, file mappings)
 * - Automatic API retry with fallback to mock data
 * - Error handling for 404/403 responses
 * - Development controls in dev mode with JSON viewer modal
 */
export const ImportProgressPage: React.FC<ImportProgressPageProps> = ({
  mappingId,
}) => {
  console.log('🎨 ImportProgressPage rendered with mappingId:', mappingId);
  const navigate = useNavigate();
  const [studyFormsMap, setStudyFormsMap] = useState<Map<number, StudyForm>>(
    new Map()
  );

  // Use centralized study forms loading
  const { forms: studyForms } = useStudyForms({
    enabled: true,
    autoLoad: true,
  });

  // Convert studyForms array to Map when forms are loaded
  useEffect(() => {
    if (studyForms.length > 0) {
      const formsMap = new Map<number, StudyForm>();
      studyForms.forEach(form => {
        formsMap.set(form.id, form);
      });
      setStudyFormsMap(formsMap);
      console.log('✅ Loaded study forms from cache:', formsMap.size);
    }
  }, [studyForms]);

  // Use custom hook for mapping data
  const {
    mappingData,
    csvFiles,
    loading,
    error,
    isRefreshing,
    isPolling,
    loadMappingData,
  } = useImportMappingData({
    mappingId,
    studyFormsMap,
  });

  const handleRefreshData = () => {
    loadMappingData(true);
  };

  const handleBackToDataImport = () => {
    navigate('/data-import/file-upload');
  };

  const breadcrumbItems = [
    {
      label: 'Data Import',
      href: '/data-import/file-upload',
      startIcon: <IconHome />,
    },
    {
      label: `Mapping ${mappingId}`,
      href: `/data-import/mapping/${mappingId}`,
    },
  ];

  if (loading && !mappingData) {
    return (
      <ImportProgressLayout
        mappingId={mappingId}
        onRefresh={handleRefreshData}
        breadcrumbItems={breadcrumbItems}
        isRefreshing={isRefreshing || isPolling}
      >
        <EmptyContent
          size="medium"
          title="Loading mapping details..."
          icon={<Spinner size={'2'} />}
        />
      </ImportProgressLayout>
    );
  }

  if (!mappingData) {
    return (
      <ImportProgressLayout
        mappingId={mappingId}
        onRefresh={handleRefreshData}
        breadcrumbItems={breadcrumbItems}
        isRefreshing={isRefreshing || isPolling}
      >
        <EmptyContent
          state="error"
          withIconBox
          icon={<IconFileSearch />}
          title="Mapping not found"
          description={`Import mapping with ID "${mappingId}" was not found.`}
        >
          <Button
            startIcon={<IconArrowLeft />}
            variant="outlined"
            as={Link}
            to="/data-import/file-upload"
          >
            Back to Data Import
          </Button>
        </EmptyContent>
      </ImportProgressLayout>
    );
  }

  if (error) {
    return (
      <ImportProgressLayout
        mappingId={mappingId}
        onRefresh={handleRefreshData}
        breadcrumbItems={breadcrumbItems}
        isRefreshing={isRefreshing || isPolling}
      >
        <EmptyContent
          size="medium"
          state="error"
          withIconBox
          icon={<IconAlertTriangleFilled />}
          title="Error"
          description={error}
        >
          <Flex gap="2" mt="3">
            <Button size="small" onClick={() => loadMappingData(true)}>
              Try Again
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBackToDataImport}
            >
              Back to Data Import
            </Button>
          </Flex>
        </EmptyContent>
      </ImportProgressLayout>
    );
  }

  return (
    <>
      <ImportProgressLayout
        mappingId={mappingId}
        onRefresh={handleRefreshData}
        breadcrumbItems={breadcrumbItems}
        isRefreshing={isRefreshing || isPolling}
        status={
          <ImportProgressStatus
            status={mappingData.status}
            description={mappingData.description}
          />
        }
        dates={
          <ImportProcessingTimeline
            createdAt={mappingData.created_at}
            processingStartedAt={mappingData.processing_started_at}
            completedAt={mappingData.completed_at}
            failedAt={mappingData.failed_at}
          />
        }
      >
        <Flex direction="column" gap="4">
          {/* Progress Card */}
          <ImportProgressCard mappingData={mappingData} />

          {/* Import Statistics */}
          <ImportStatisticsCards mappingData={mappingData} />

          {/* File Mappings */}
          <ImportFileMappings
            fileMappings={mappingData.file_mappings}
            csvFiles={csvFiles}
            studyForms={studyFormsMap}
            importValidationErrors={mappingData.import_validation_errors}
          />

          {/* Info Alert */}
          <VisuallyHidden>
            <Alert color="info" title="Import Processing">
              <Flex direction="column" gap="2">
                The mapping has been created successfully. The backend will
                automatically start processing the import. You can monitor the
                progress here or check back later.
              </Flex>
            </Alert>
          </VisuallyHidden>
        </Flex>
      </ImportProgressLayout>

      {/* Import Mapping Debug Panel */}
      <ImportMappingDebugPanel
        mappingId={mappingId}
        mappingData={mappingData}
        onRefreshData={handleRefreshData}
      />
    </>
  );
};
